import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/api';
import LoadingSpinner from '../Components/LoadingSpinner';

function KPI({ label, value, active, onClick }) {
  return (
    <div className="col-md-3 col-sm-6 mb-3">
      <button
        type="button"
        className={`card text-center shadow-sm w-100 border ${active ? 'border-primary' : ''}`}
        onClick={onClick}
      >
        <div className="card-body">
          <h2 className="card-title mb-1">{value}</h2>
          <p className="card-text text-muted mb-0">{label}</p>
        </div>
      </button>
    </div>
  );
}

function SummaryRow({ left, right }) {
  return (
    <div className="d-flex justify-content-between small text-muted">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

function SuperAdminPanel() {
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [listings, setListings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const tabMap = useMemo(
    () => ({
      summary: 'Summary',
      users: 'Users',
      contacts: 'Contacts',
      listings: 'Listings',
      tasks: 'Tasks',
    }),
    []
  );

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const result = await apiFetch('getActivitySummary');
        if (result.success) {
          setSummary(result.summary);
        }
      } catch (err) {
        console.error('Failed to fetch summary', err);
      }
    };

    fetchSummary();
  }, []);

  const loadTabData = async (tab) => {
    if (tab === 'users' && users.length) return;
    if (tab === 'contacts' && contacts.length) return;
    if (tab === 'listings' && listings.length) return;
    if (tab === 'tasks' && tasks.length) return;

    setLoading(true);
    try {
      if (tab === 'users') {
        const res = await apiFetch('fetchUsers');
        if (res.success) setUsers(res.users || []);
      } else if (tab === 'contacts') {
        const res = await apiFetch('fetchAllContacts');
        if (res.success) setContacts(res.contacts || []);
      } else if (tab === 'listings') {
        const res = await apiFetch('fetchListings');
        if (res.success) setListings(res.listings || []);
      } else if (tab === 'tasks') {
        const res = await apiFetch('fetchAllTasks');
        if (res.success) setTasks(res.tasks || []);
      }
    } catch (err) {
      console.error(`Failed to load ${tab}:`, err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'summary') {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  if (!summary) {
    return <LoadingSpinner message="Loading summary…" />;
  }

  const renderList = () => {
    if (loading) return <LoadingSpinner message={`Loading ${tabMap[activeTab]}…`} />;

    switch (activeTab) {
      case 'users':
        return (
          <div className="list-group shadow-sm">
            {users.map((u) => (
              <div className="list-group-item" key={u.id || u.user_id}>
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-bold">
                      {u.firstName || u.first_name} {u.lastName || u.last_name}
                    </div>
                    <SummaryRow left="Email" right={u.email} />
                    <SummaryRow left="Role" right={u.role} />
                    <SummaryRow left="Brokerage" right={u.brokerageName || u.brokerage_name || '—'} />
                  </div>
                  <div className="text-end small text-muted">
                    <div>User ID: {u.userId || u.user_id || u.id}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'contacts':
        return (
          <div className="list-group shadow-sm">
            {contacts.map((c) => (
              <div className="list-group-item" key={c.id}>
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-bold">{c.name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()}</div>
                    <SummaryRow left="Email" right={c.email || '—'} />
                    <SummaryRow left="Phone" right={c.phone || '—'} />
                    <SummaryRow left="Role" right={c.role || '—'} />
                  </div>
                  <div className="text-end small text-muted">
                    <div>Contact ID: {c.id}</div>
                    <div>User ID: {c.user_id ?? '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'listings':
        return (
          <div className="list-group shadow-sm">
            {listings.map((l) => (
              <div className="list-group-item" key={l.listingId || l.propertyId}>
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-bold">{l.streetAddress}, {l.city} {l.state}</div>
                    <SummaryRow left="Price" right={l.listPrice ? `$${l.listPrice}` : '—'} />
                    <SummaryRow left="Status" right={l.listingStatus || l.status || '—'} />
                    <SummaryRow left="Agent" right={l.listingUserId || l.creatorID || '—'} />
                  </div>
                  <div className="text-end small text-muted">
                    <div>Property ID: {l.propertyId}</div>
                    <div>Listing ID: {l.listingId || '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'tasks':
        return (
          <div className="list-group shadow-sm">
            {tasks.map((t) => (
              <div className="list-group-item" key={t.task_id}>
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-bold">{t.task_type || 'Task'}</div>
                    <SummaryRow left="Contact" right={t.contact_name || t.contact_id || '—'} />
                    <SummaryRow left="Due" right={t.due_date || '—'} />
                    <SummaryRow left="Status" right={t.status || 'open'} />
                  </div>
                  <div className="text-end small text-muted">
                    <div>Task ID: {t.task_id}</div>
                    <div>Urgency: {t.urgency || '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mt-4">
      <h1 className="mb-3">Super Admin Panel</h1>
      <div className="row">
        <KPI label="Users" value={summary.users} active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
        <KPI label="Contacts" value={summary.contacts} active={activeTab === 'contacts'} onClick={() => setActiveTab('contacts')} />
        <KPI label="Listings" value={summary.listings} active={activeTab === 'listings'} onClick={() => setActiveTab('listings')} />
        <KPI label="Tasks" value={summary.tasks} active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')} />
      </div>

      <div className="d-flex gap-2 mb-3">
        {Object.entries(tabMap).map(([key, label]) => (
          <button
            key={key}
            className={`btn btn-sm ${activeTab === key ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'summary' ? (
        <div className="row">
          <KPI label="Users" value={summary.users} />
          <KPI label="Contacts" value={summary.contacts} />
          <KPI label="Listings" value={summary.listings} />
          <KPI label="Tasks" value={summary.tasks} />
        </div>
      ) : (
        renderList()
      )}
    </div>
  );
}

export default SuperAdminPanel;
